import * as _ from 'underscore';
import { ServerUrls } from '../../app/ServerUrls';
import { Messages } from '../../codegen/Localize';
import { FeatureFlags } from '../FeatureFlags';
import { IHost } from '../models/IHost';
import { Module } from '../Module';
import { WorkerStatusType } from '../ServerApi/requests/GetWorkerProcessStatus';
import { IFeatureFlagsDoNotUse } from '../ServerApi/types/IFeatureFlagsDoNotUse';
import { WorkerProcessType } from '../ServerApi/types/IWorkerProcess';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ConfirmActionDialog } from '../services/ConfirmActionDialog';
import { RebuildSearchIndexService } from '../services/RebuildSearchIndexService';
import { ServerInfoService } from '../services/ServerInfoService';
import { ServerService } from '../services/ServerService';

export interface IServerStatusScope extends ng.IScope {
  hosts: IHost[];
  components: string[];
  legendStatuses: string[];
  refreshButtonBusy: boolean;
  showAdminViews: boolean;
  showZiplog: boolean;
  showRebuildSearchIndex: boolean;
  showMultinodePrimaryWorkerLabels: () => boolean;
  isPreferred: (component: string, host: IHost) => boolean;
  refreshStatuses: () => void;
  rebuildSearchIndexPercent: () => number;
  rebuildSearchIndexPending: () => boolean;
  rebuildSearchIndex: () => void;
  hideStatsForLoadTimes: () => boolean;
  hidePerformanceofViews: () => boolean;
  hidePerformanceofFlowRuns: () => boolean;
  analysisUrl: (sheetUrlId: string) => string;
}

export class ServerStatusCtrl {
  public static refreshProcessStatusEvent = 'refreshProcessStatus';
  private static legendStatuses = [
    WorkerStatusType.active,
    WorkerStatusType.busy,
    WorkerStatusType.passive,
    WorkerStatusType.unlicensed,
    WorkerStatusType.down,
    WorkerStatusType.external,
    WorkerStatusType.statusNotAvailable,
 ];
  private static WorkerProcessTypes = [
    WorkerProcessType.clusterController, // cluster controller should be first
    WorkerProcessType.gateway,
    WorkerProcessType.vizportal,
    WorkerProcessType.wgServer,
    WorkerProcessType.vizqlServer,
    WorkerProcessType.cacheServer,
    WorkerProcessType.searchServer,
    WorkerProcessType.backgrounder,
    WorkerProcessType.dataServer,
    WorkerProcessType.dataEngine, // TFS 646124 - Remove the feature flag, remove the dataengine.
    WorkerProcessType.hyper,
    WorkerProcessType.fileStore,
    WorkerProcessType.repository,
    WorkerProcessType.flowProcessor
  ];

  private featureFlagsDoNotUse: IFeatureFlagsDoNotUse;
  private wgEnabled: boolean;
  private flowProcessorEnabled: boolean;

  public static $inject = [
    '$scope',
    '$translate',
    'BrowserTitleService',
    'ServerService',
    'RebuildSearchIndexService',
    'ConfirmActionDialog',
    'ServerInfoService'
  ];

  constructor(
    $scope: IServerStatusScope,
    $translate: ng.translate.ITranslateService,
    BrowserTitleService: BrowserTitleService,
    server: ServerService,
    rebuildSearchIndexService: RebuildSearchIndexService,
    confirmActionDialog: ConfirmActionDialog,
    serverInfo: ServerInfoService
  ) {
    BrowserTitleService.setTitleWithTranslatedSpace({translatedSpaceName: Messages.serverStatus()});

    // Under TSM, ziplog and rebuild search index is done through TSM, not Vizportal
    $scope.showZiplog = !FeatureFlags.tableauServicesManagerEnabled && server.isTabAdminServiceEnabled();
    $scope.showAdminViews = !server.isAdminViewsDisabled();
    $scope.showRebuildSearchIndex = !FeatureFlags.tableauServicesManagerEnabled;

    // Server Status Grid
    this.featureFlagsDoNotUse = server.featureFlagsDoNotUse(); // move these flags to server settings (TFS: 653138)
    this.wgEnabled = server.isWgEnabled();
    this.flowProcessorEnabled = server.flowsOnServerEnabled();
    $scope.showMultinodePrimaryWorkerLabels = () => {
      return $scope.hosts.length > 1 && !FeatureFlags.tableauServicesManagerEnabled;
    };
    $scope.components = this.getComponents($scope.hosts);
    $scope.legendStatuses = ServerStatusCtrl.legendStatuses;
    $scope.isPreferred = (component: string, host: IHost): boolean => {
      return component === WorkerProcessType.repository
        && this.featureFlagsDoNotUse.ha2Enabled
        && host.processes
        && host.processes[WorkerProcessType.repository]
        && host.processes[WorkerProcessType.repository][0].preferred;
    };
    $scope.refreshStatuses = () => {
      $scope.refreshButtonBusy = true;
      serverInfo.fetch()
        .then((hosts: IHost[]) => {
          $scope.hosts = hosts; // Update hosts if server config has changed
          $scope.$broadcast(ServerStatusCtrl.refreshProcessStatusEvent);
        })
        .finally(() => $scope.refreshButtonBusy = false);
    };

    // Rebuild Search Index
    $scope.rebuildSearchIndexPending = () => rebuildSearchIndexService.getRebuildSearchIndexPending();
    $scope.rebuildSearchIndexPercent = () => rebuildSearchIndexService.getRebuildSearchIndexPercent();
    $scope.rebuildSearchIndex = () => {
      confirmActionDialog.open({
        title: $translate.instant('rebuildSearchIndexConfirmTitle'),
        bodyText: $translate.instant('rebuildSearchIndexConfirmMessage'),
        actionName: $translate.instant('rebuildSearchIndexConfirmButton'),
        isDeleteAction: true,
        action: () => {
          rebuildSearchIndexService.rebuildSearchIndex();
          // TODO: handle error, message $translate.instant('rebuildSearchIndexError')
        }
      });
    };
    $scope.$on('$destroy', () => rebuildSearchIndexService.cancelPoll());

    $scope.hideStatsForLoadTimes  = () => {
      return server.hideAdminVizStatsForLoadTimes();
    };

    $scope.hidePerformanceofViews  = () => {
      return server.hideAdminVizPerformanceofViews();
    };

    $scope.hidePerformanceofFlowRuns  = () => {
      return server.hideAdminVizPerformanceofFlowRuns();
    };

    $scope.analysisUrl = (sheetUrlId: string) => {
      return ServerUrls.getInstance().serverStatusVizUrl(sheetUrlId);
    };
  }

  private getComponents(hosts: IHost[]): string[] {
    return _.without(ServerStatusCtrl.WorkerProcessTypes,
        ((this.featureFlagsDoNotUse && !this.featureFlagsDoNotUse.ha2Enabled) || (this.featureFlagsDoNotUse && !this.featureFlagsDoNotUse.ha2FilestoreEnabled) ? WorkerProcessType.fileStore : ''),
        ((this.featureFlagsDoNotUse && !this.featureFlagsDoNotUse.ha2Enabled) || hosts.length === 1 ? WorkerProcessType.clusterController : ''),
        (!this.wgEnabled ? WorkerProcessType.wgServer : ''),
        // TFS 646124 - Remove the feature flag, remove this check.
        (FeatureFlags.hyperOnServerEnabled ? WorkerProcessType.dataEngine : WorkerProcessType.hyper),
        // Tableau Prep Server Flow Processor
        (!this.flowProcessorEnabled ? WorkerProcessType.flowProcessor : '')
      );
  }

}

Module.instance().controller('ServerStatusCtrl', ServerStatusCtrl);
